import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserData } from "@/pages/Index";

interface ProductMatchProps {
  userData: UserData;
  onNext: () => void;
}

const getConcernLabel = (concern: string) => {
  const map: Record<string, string> = {
    wrinkles: "rimpels en fijne lijntjes",
    firmness: "slappe huid en verlies van stevigheid",
    dullness: "een doffe, vermoeide teint",
    all: "meerdere huidklachten tegelijk",
  };
  return map[concern] || "jouw huidklachten";
};

const getAgeLabel = (age: string) => {
  if (!age) return "50+";
  return age;
};

const getCollagenAt = (age: number): number => {
  if (age <= 25) return 100;
  if (age <= 50) return Math.round(100 - (age - 25) * 1);
  const at50 = 75;
  return Math.round(Math.max(10, at50 - (age - 50) * 2));
};

const getUserAge = (ageRange: string): number => {
  const map: Record<string, number> = {
    "25–35": 30, "35–45": 40, "45–55": 50, "55–65": 60, "65–75": 70, "75–85": 80, "85–95": 90,
  };
  return map[ageRange] || 55;
};

const getAnalysisSteps = (userData: UserData) => {
  const userAge = getUserAge(userData.age);
  const collagen = getCollagenAt(userAge);

  return [
    `Huidprofiel voor leeftijd ${getAgeLabel(userData.age)} laden...`,
    `Collageenniveau berekend: ${collagen}%...`,
    `Jouw aangegeven huidklachten analyseren...`,
    "Klinische resultaten vergelijken...",
    "Persoonlijk behandelplan samenstellen...",
  ];
};

const ProductMatch = ({ userData, onNext }: ProductMatchProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [done, setDone] = useState(false);
  const analysisSteps = getAnalysisSteps(userData);

  useEffect(() => {
    const timers = analysisSteps.map((_, i) =>
      setTimeout(() => setCurrentStep(i + 1), 800 * (i + 1))
    );
    timers.push(
      setTimeout(() => setDone(true), 800 * analysisSteps.length + 500)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  const progress = Math.round((currentStep / analysisSteps.length) * 100);
  const userAge = getUserAge(userData.age);
  const collagen = getCollagenAt(userAge);

  return (
    <div className="text-center">
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl md:text-4xl font-serif font-medium text-foreground mb-2 leading-tight"
      >
        We zoeken het beste product voor jou
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-foreground/80 font-sans text-lg md:text-xl mb-6 md:mb-10"
      >
        Op basis van jouw profiel en {getConcernLabel(userData.concern)}
      </motion.p>

      {/* Analysis animation */}
      {!done && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="insight-card mb-5 md:mb-8"
        >
          {/* Progress bar */}
          <div className="w-full h-2 md:h-3 bg-secondary rounded-full mb-5 md:mb-8 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: "linear-gradient(90deg, hsl(var(--rose)), hsl(var(--rose-light)))" }}
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>

          {/* Steps list */}
          <div className="space-y-3 md:space-y-4 text-left">
            {analysisSteps.map((step, i) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: -10 }}
                animate={{
                  opacity: i < currentStep ? 1 : i === currentStep ? 0.5 : 0.2,
                  x: 0,
                }}
                transition={{ delay: 0.1 * i, duration: 0.3 }}
                className="flex items-center gap-3 font-sans text-lg"
              >
                {i < currentStep ? (
                  <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-white text-sm flex-shrink-0">
                    ✓
                  </span>
                ) : i === currentStep ? (
                  <span className="w-6 h-6 rounded-full border-2 border-accent flex-shrink-0 animate-pulse" />
                ) : (
                  <span className="w-6 h-6 rounded-full border-2 border-border flex-shrink-0" />
                )}
                <span className={i < currentStep ? "text-foreground" : "text-foreground/70"}>
                  {step}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Result */}
      <AnimatePresence>
        {done && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="insight-card mb-5 md:mb-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <p className="font-sans text-sm uppercase tracking-wider text-accent font-bold mb-3">
                  Jouw beste match
                </p>
                <p className="font-serif text-2xl md:text-4xl text-foreground mb-3 leading-tight">
                  1 product gevonden dat aansluit bij jouw profiel
                </p>
                <p className="font-sans text-lg text-foreground/80 leading-relaxed">
                  Op basis van jouw leeftijd ({getAgeLabel(userData.age)}), collageenniveau ({collagen}%) en jouw aangegeven klachten
                  hebben we een behandeling geselecteerd die klinisch bewezen effectief is
                  voor {getConcernLabel(userData.concern)}.
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <button onClick={onNext} className="btn-cta">
                Bekijk mijn persoonlijke aanbeveling
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductMatch;
